/**
 * The two things every primitive in this folder needs, and the only two.
 *
 * `mockups/assets/CONTRACT.md` is a closed vocabulary: a page composes
 * primitives and never writes a class. That rule is enforced by what these
 * components *do not* accept — there is no `className`, no `style`, and no
 * `...rest` of arbitrary DOM props. What a page may still set is an anchor id
 * (`#wo-2026-014` is a real link target on `client/requests.html`) and ARIA,
 * which is about meaning rather than appearance.
 *
 * `cx` is here rather than copied into twenty-one files. It is the only
 * shared helper: everything else in this folder is a family.
 */

import { cloneElement, isValidElement, type AriaAttributes, type ReactNode } from 'react'

/**
 * Join the class names that survive. `false` and `undefined` drop out, so a
 * modifier reads as `cond && 'card--need'` at the call site.
 *
 * Nothing left is `undefined` rather than `''`, so a cell with no modifier
 * renders `<td>` and not `<td class="">` — which is what the reconciliation
 * check would otherwise have to learn to ignore.
 */
export function cx(...parts: readonly (string | false | undefined)[]): string | undefined {
  const kept = parts.filter((part): part is string => typeof part === 'string' && part !== '')
  return kept.length === 0 ? undefined : kept.join(' ')
}

/**
 * What a page may pass to any primitive: an anchor id, and ARIA attributes.
 *
 * Spreading this onto the element is deliberate and safe — `AriaAttributes` has
 * no `className`, no `style` and no event handler in it, and TypeScript's excess
 * property check on a JSX literal refuses anything that is not in the union.
 */
export type Common = AriaAttributes & { readonly id?: string | undefined }

/**
 * `asChild` — hand the class to the caller's own element instead of rendering one.
 *
 * The shadcn/Radix idiom, and it is here for one reason: an internal link in this
 * app is a TanStack `<Link to="…">`, not an `<a href>`, and a page that wrote
 * `<Link className="linkish">` to get around that would be writing a class —
 * which is what this whole folder exists to make unnecessary. So
 * `<Linkish asChild><Link to="/studio">studio portal</Link></Linkish>` renders
 * the router's element wearing the contract's class, and nothing in here knows
 * what a router is.
 *
 * One element child, exactly. A fragment or a string has no `className` to give.
 */
export function asChildElement(
  children: ReactNode,
  className: string,
  extra: Readonly<Record<string, unknown>> = {},
): ReactNode {
  if (!isValidElement<Record<string, unknown>>(children)) {
    throw new Error('asChild expects exactly one element child to hand the class to.')
  }
  return cloneElement(children, { ...extra, className })
}
